import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useWatch } from 'react-hook-form'
import type {
  Control,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
  FieldErrors,
  Path,
} from 'react-hook-form'

import { formatUsPhone, usPhoneDigits } from '#/shared/lib/format'
import { MANAGE_FIELD_KEY, useIsFieldHidden } from '#/shared/lib/manage-fields'
import type { ManageFieldKey } from '#/shared/lib/manage-fields'
import { cn } from '#/shared/lib/utils'
import { ComboboxBadgeSuffix, ComboboxLinkSuffix } from '#/shared/ui/combobox'
import type { ComboboxOption } from '#/shared/ui/combobox'
import { ChevronDownIcon, DestinationIcon, OriginIcon } from '#/shared/ui/icons'
import { Input } from '#/shared/ui/input'
import { Select } from '#/shared/ui/select'
import type { CustomerListItem } from '#/shared/api/generated/shipment/models'
import { LOCATION_TYPE_VALUES } from '../model/location-schema'
import type { LocationData, LocationFormData } from '../model/location-schema'
import { BusinessCombobox } from './business-combobox'
import { CityCombobox } from './city-combobox'
import { ContactCombobox } from './contact-combobox'
import { CustomerSearchCombobox } from './customer-search-combobox'
import { ZipCombobox } from './zip-combobox'
import { useZipLookup } from '../model/use-zip-lookup'
import type { ZipLookupResult } from '../model/use-zip-lookup'

import type {
  LocationFlags,
  LocationVariant,
  UseLocationFlagsResult,
} from '../model/use-location-flags'

interface LocationCardProps {
  control: Control<LocationFormData>
  register: UseFormRegister<LocationFormData>
  setValue?: UseFormSetValue<LocationFormData>
  clearErrors?: UseFormClearErrors<LocationFormData>
  errors: FieldErrors<LocationFormData>
  businessOptions?: ComboboxOption[]
  contactOptions?: ComboboxOption[]
  bare?: boolean
  compact?: boolean
  cityZipOnly?: boolean
  cityZipTypeOnly?: boolean
  onSameAsCustomer?: (
    variant: LocationVariant,
    field: 'businessName' | 'contact',
  ) => void
  flags?: UseLocationFlagsResult
  /**
   * Collapses each block's contact fields (contact/phone/mobile/note) behind a
   * "More details" toggle when they already have values; empty blocks stay
   * open. Expand-only — there is no re-collapse control.
   */
  collapsibleContacts?: boolean
}

const EMPTY_LOCATION_FLAGS: LocationFlags = {
  origin: false,
  destination: false,
}

const NOOP_FLAGS: UseLocationFlagsResult = {
  terminalSaved: EMPTY_LOCATION_FLAGS,
  contactSaved: EMPTY_LOCATION_FLAGS,
  onSaveAsTerminal: () => {},
  onClearTerminal: () => {},
  onSaveAsContact: () => {},
  onClearContact: () => {},
}

const FIELD_PROPS = {
  inputSize: 'sm' as const,
  labelSize: 'sm' as const,
  clearable: true,
}

function useTypeOptions() {
  const { t } = useTranslation()
  return LOCATION_TYPE_VALUES.map((value) => ({
    value,
    label: t(`orderCreate.location.typeOptions.${value}`),
  }))
}

function SectionHeader({ variant }: { variant: LocationVariant }) {
  const { t } = useTranslation()
  const Icon = variant === 'origin' ? OriginIcon : DestinationIcon
  const iconColor = variant === 'origin' ? 'text-blue-500' : 'text-orange-500'
  return (
    <div className="flex h-8 items-center gap-1.25 px-2.5">
      <Icon className={cn('size-4 shrink-0', iconColor)} />
      <span className="text-xs uppercase text-gray-500">
        {t(`orderCreate.location.${variant}.title`)}
      </span>
    </div>
  )
}

/**
 * Sidebar (Figma) block layout: the origin/destination pin in a left gutter,
 * aligned with the first field's input, and the fields to its right. No
 * "Origin"/"Destination" text label. Shared by the full and city-only sections.
 */
function GutterSection({
  variant,
  children,
}: {
  variant: LocationVariant
  children: React.ReactNode
}) {
  const Icon = variant === 'origin' ? OriginIcon : DestinationIcon
  const iconColor = variant === 'origin' ? 'text-blue-500' : 'text-orange-500'
  return (
    <div className="flex gap-2.5 px-2.5">
      <div className="flex h-12 shrink-0 items-end pb-2">
        <Icon className={cn('size-4.5', iconColor)} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">{children}</div>
    </div>
  )
}

interface SectionProps {
  variant: LocationVariant
  control: Control<LocationFormData>
  register: UseFormRegister<LocationFormData>
  setValue?: UseFormSetValue<LocationFormData>
  clearErrors?: UseFormClearErrors<LocationFormData>
  errors: FieldErrors<LocationFormData>
  businessOptions: ComboboxOption[]
  contactOptions: ComboboxOption[]
  onSameAsCustomer?: (
    variant: LocationVariant,
    field: 'businessName' | 'contact',
  ) => void
  terminalSaved?: boolean
  contactSaved?: boolean
  onSaveAsTerminal?: (variant: LocationVariant) => void
  onSaveAsContact?: (variant: LocationVariant) => void
  onClearTerminal?: (variant: LocationVariant) => void
  onClearContact?: (variant: LocationVariant) => void
  collapsibleContacts?: boolean
}

function path<TKey extends keyof LocationFormData['origin']>(
  variant: LocationVariant,
  key: TKey,
): Path<LocationFormData> {
  return `${variant}.${key}` as Path<LocationFormData>
}

function customerContactName(customer: CustomerListItem): string {
  return [customer.first_name, customer.last_name].filter(Boolean).join(' ')
}

const CUSTOMER_TO_LOCATION_MAP: ReadonlyArray<
  [keyof LocationData, keyof CustomerListItem]
> = [
  ['address', 'address'],
  ['city', 'city'],
  ['state', 'state'],
  ['zip', 'zip'],
  ['phone', 'phone'],
  ['mobile', 'secondary_phone'],
  ['additionalNote', 'note'],
  ['type', 'company_type'],
]

function applyCustomerToLocation(
  variant: LocationVariant,
  customer: CustomerListItem,
  setValue: UseFormSetValue<LocationFormData>,
) {
  const setOpts = { shouldValidate: true, shouldDirty: true } as const
  const contact = customerContactName(customer)
  const businessName = customer.company ?? contact
  setValue(path(variant, 'businessName'), businessName, setOpts)
  if (contact) setValue(path(variant, 'contact'), contact, setOpts)
  for (const [target, source] of CUSTOMER_TO_LOCATION_MAP) {
    const value = customer[source]
    if (typeof value === 'string' && value)
      setValue(path(variant, target), value, setOpts)
  }
}

const LOCATION_FIELDS: ReadonlyArray<keyof LocationData> = [
  'businessName',
  'address',
  'city',
  'state',
  'zip',
  'contact',
  'phone',
  'mobile',
  'additionalNote',
  'type',
]

function clearLocationFields(
  variant: LocationVariant,
  setValue: UseFormSetValue<LocationFormData>,
  clearErrors?: UseFormClearErrors<LocationFormData>,
) {
  const setOpts = { shouldValidate: false, shouldDirty: true } as const
  for (const field of LOCATION_FIELDS) {
    setValue(path(variant, field), '', setOpts)
  }
  if (clearErrors) {
    for (const field of LOCATION_FIELDS) {
      clearErrors(path(variant, field))
    }
  }
}

function applyContactCustomerToLocation(
  variant: LocationVariant,
  customer: CustomerListItem,
  setValue: UseFormSetValue<LocationFormData>,
) {
  const setOpts = { shouldValidate: true, shouldDirty: true } as const
  const contact = customerContactName(customer)
  if (contact) setValue(path(variant, 'contact'), contact, setOpts)
  if (customer.phone)
    setValue(path(variant, 'phone'), usPhoneDigits(customer.phone), setOpts)
  if (customer.secondary_phone)
    setValue(
      path(variant, 'mobile'),
      usPhoneDigits(customer.secondary_phone),
      setOpts,
    )
  if (typeof customer.note === 'string' && customer.note)
    setValue(path(variant, 'additionalNote'), customer.note, setOpts)
}

type SectionErrors = NonNullable<FieldErrors<LocationFormData>[LocationVariant]>

function FieldRow({ children }: { children: React.ReactNode }) {
  return <div className="flex items-start gap-2.5">{children}</div>
}

function FieldCell({
  width,
  children,
}: {
  width: string
  children: React.ReactNode
}) {
  return <div className={width}>{children}</div>
}

function BusinessAddressRow({
  variant,
  control,
  register,
  setValue,
  clearErrors,
  fieldErrors,
  onSameAsCustomer,
  onSaveAsTerminal,
  onClearTerminal,
  terminalSaved,
}: SectionProps & { fieldErrors: SectionErrors }) {
  const { t } = useTranslation()
  const showBusinessName = !useIsFieldHidden(BUSINESS_NAME_KEY[variant])
  const addressLabel = t(
    variant === 'origin'
      ? 'orderCreate.location.origin.address'
      : 'orderCreate.location.destination.address',
  )
  const typeCell = !showBusinessName ? (
    <FieldCell width="w-27.5 shrink-0">
      <TypeCell variant={variant} control={control} fieldErrors={fieldErrors} />
    </FieldCell>
  ) : null
  const businessSuffix = terminalSaved ? (
    <ComboboxBadgeSuffix
      label={t('orderCreate.location.newTerminalBadge')}
      onClick={() => onClearTerminal?.(variant)}
    />
  ) : onSameAsCustomer ? (
    <ComboboxLinkSuffix
      label={t('orderCreate.location.sameAsCustomer')}
      onClick={() => onSameAsCustomer(variant, 'businessName')}
    />
  ) : undefined
  return (
    <FieldRow>
      {showBusinessName && (
        <FieldCell width="min-w-0 flex-1">
          <Controller
            control={control}
            name={path(variant, 'businessName')}
            render={({ field }) => (
              <BusinessCombobox
                label={t('orderCreate.location.businessName')}
                placeholder={t(
                  'orderCreate.location.placeholders.businessName',
                )}
                value={field.value as string}
                onValueChange={field.onChange}
                onCustomerSelect={(customer) => {
                  if (setValue)
                    applyCustomerToLocation(variant, customer, setValue)
                }}
                onClear={
                  setValue
                    ? () => clearLocationFields(variant, setValue, clearErrors)
                    : undefined
                }
                error={!!fieldErrors.businessName}
                suffix={businessSuffix}
                searchDisabled={terminalSaved}
                action={{
                  label: t('orderCreate.location.saveAsTerminal'),
                  onClick: onSaveAsTerminal
                    ? () => onSaveAsTerminal(variant)
                    : undefined,
                }}
              />
            )}
          />
        </FieldCell>
      )}
      <FieldCell width="min-w-0 flex-1">
        <Input
          {...FIELD_PROPS}
          {...register(path(variant, 'address'))}
          label={addressLabel}
          error={!!fieldErrors.address}
        />
      </FieldCell>
      {typeCell}
    </FieldRow>
  )
}

function TypeCell({
  variant,
  control,
  fieldErrors,
}: {
  variant: LocationVariant
  control: Control<LocationFormData>
  fieldErrors: SectionErrors
}) {
  const { t } = useTranslation()
  const typeOptions = useTypeOptions()
  return (
    <Controller
      control={control}
      name={path(variant, 'type')}
      render={({ field }) => (
        <Select
          selectSize="sm"
          labelSize="sm"
          label={t('orderCreate.location.type')}
          placeholder={t('orderCreate.location.placeholders.type')}
          options={typeOptions}
          value={field.value as string}
          onValueChange={field.onChange}
          error={!!fieldErrors.type}
        />
      )}
    />
  )
}

function ZipCell({
  variant,
  control,
  fieldErrors,
  onLoadingChange,
  onGeocoded,
}: {
  variant: LocationVariant
  control: Control<LocationFormData>
  fieldErrors: SectionErrors
  onLoadingChange: (loading: boolean) => void
  onGeocoded: (geocoded: ZipLookupResult) => void
}) {
  const { t } = useTranslation()
  return (
    <Controller
      control={control}
      name={path(variant, 'zip')}
      render={({ field }) => (
        <ZipCombobox
          label={`${t('orderCreate.location.zip')} *`}
          placeholder={t('orderCreate.location.placeholders.zip')}
          value={field.value as string}
          onValueChange={field.onChange}
          onLoadingChange={onLoadingChange}
          onGeocodedSelect={onGeocoded}
          error={!!fieldErrors.zip}
        />
      )}
    />
  )
}

function CityCell({
  variant,
  control,
  setValue,
  fieldErrors,
  zipLoading,
  lookupPlaceholder,
  warning,
  onCityTyped,
  onCitySelected,
}: {
  variant: LocationVariant
  control: Control<LocationFormData>
  setValue?: UseFormSetValue<LocationFormData>
  fieldErrors: SectionErrors
  zipLoading: boolean
  lookupPlaceholder: string
  warning?: string
  onCityTyped?: () => void
  onCitySelected?: () => void
}) {
  const { t } = useTranslation()
  return (
    <Controller
      control={control}
      name={path(variant, 'city')}
      render={({ field }) => (
        <div className="flex flex-col gap-0.5">
          <CityCombobox
            label={`${t('orderCreate.location.city')} *`}
            placeholder={zipLoading ? lookupPlaceholder : undefined}
            value={field.value as string}
            onValueChange={(next) => {
              onCityTyped?.()
              field.onChange(next)
            }}
            onGeocodedSelect={(geocoded) => {
              if (!setValue) return
              const setOpts = {
                shouldValidate: true,
                shouldDirty: true,
              } as const
              setValue(path(variant, 'city'), geocoded.city, setOpts)
              setValue(path(variant, 'state'), geocoded.state, setOpts)
              onCitySelected?.()
            }}
            error={!!fieldErrors.city}
            disabled={zipLoading}
          />
          {warning && <p className="text-2xs text-orange-500">{warning}</p>}
        </div>
      )}
    />
  )
}

function CityStateZipRow({
  variant,
  control,
  register,
  setValue,
  fieldErrors,
  withType = false,
}: {
  variant: LocationVariant
  control: Control<LocationFormData>
  register: UseFormRegister<LocationFormData>
  setValue?: UseFormSetValue<LocationFormData>
  fieldErrors: SectionErrors
  withType?: boolean
}) {
  const { t } = useTranslation()
  const [zipLoading, setZipLoading] = React.useState(false)
  const lookupPlaceholder = t('orderCreate.location.lookingUp')

  const city = useWatch({ control, name: path(variant, 'city') }) as string
  const zip = useWatch({ control, name: path(variant, 'zip') }) as string
  const { results } = useZipLookup(zip)
  const expected = results.find((r) => r.zip === zip.trim())
  const cityUserEditedRef = React.useRef(false)
  const cityWarning =
    cityUserEditedRef.current &&
    expected &&
    city.trim() &&
    city.trim().toLowerCase() !== expected.city.toLowerCase()
      ? t('orderCreate.location.cityZipMismatch', { zip: zip.trim() })
      : undefined

  const handleZipGeocoded = (geocoded: ZipLookupResult) => {
    if (!setValue) return
    const setOpts = { shouldValidate: true, shouldDirty: true } as const
    setValue(path(variant, 'city'), geocoded.city, setOpts)
    setValue(path(variant, 'state'), geocoded.state, setOpts)
    cityUserEditedRef.current = false
  }

  const handleCityTyped = () => {
    cityUserEditedRef.current = true
  }

  const handleCitySelected = () => {
    cityUserEditedRef.current = false
  }

  return (
    <FieldRow>
      <FieldCell width="min-w-0 flex-1">
        <CityCell
          variant={variant}
          control={control}
          setValue={setValue}
          fieldErrors={fieldErrors}
          zipLoading={zipLoading}
          lookupPlaceholder={lookupPlaceholder}
          warning={cityWarning}
          onCityTyped={handleCityTyped}
          onCitySelected={handleCitySelected}
        />
      </FieldCell>
      <FieldCell width="w-17.5 shrink-0">
        <Input
          {...FIELD_PROPS}
          {...register(path(variant, 'state'))}
          label={`${t('orderCreate.location.state')} *`}
          placeholder={zipLoading ? lookupPlaceholder : undefined}
          error={!!fieldErrors.state}
          disabled={zipLoading}
        />
      </FieldCell>
      <FieldCell width="w-20 shrink-0">
        <ZipCell
          variant={variant}
          control={control}
          fieldErrors={fieldErrors}
          onLoadingChange={setZipLoading}
          onGeocoded={handleZipGeocoded}
        />
      </FieldCell>
      {withType && (
        <FieldCell width="w-27.5 shrink-0">
          <TypeCell
            variant={variant}
            control={control}
            fieldErrors={fieldErrors}
          />
        </FieldCell>
      )}
    </FieldRow>
  )
}

function ContactCell({
  variant,
  control,
  setValue,
  fieldErrors,
  onSameAsCustomer,
  onSaveAsContact,
  onClearContact,
  contactSaved,
}: SectionProps & { fieldErrors: SectionErrors }) {
  const { t } = useTranslation()
  const contactSuffix = contactSaved ? (
    <ComboboxBadgeSuffix
      label={t('orderCreate.location.newContactBadge')}
      onClick={() => onClearContact?.(variant)}
    />
  ) : onSameAsCustomer ? (
    <ComboboxLinkSuffix
      label={t('orderCreate.location.sameAsCustomer')}
      onClick={() => onSameAsCustomer(variant, 'contact')}
    />
  ) : undefined
  return (
    <FieldCell width="min-w-0 grow-[2] basis-0">
      <Controller
        control={control}
        name={path(variant, 'contact')}
        render={({ field }) => (
          <ContactCombobox
            label={t('orderCreate.location.contact')}
            value={field.value as string}
            onValueChange={field.onChange}
            onCustomerSelect={(customer) => {
              if (setValue)
                applyContactCustomerToLocation(variant, customer, setValue)
            }}
            error={!!fieldErrors.contact}
            suffix={contactSuffix}
            searchDisabled={contactSaved}
            action={{
              label: t('orderCreate.location.saveAsContact'),
              onClick: onSaveAsContact
                ? () => onSaveAsContact(variant)
                : undefined,
            }}
          />
        )}
      />
    </FieldCell>
  )
}

function phoneTag(value: string | null | undefined): string | undefined {
  const formatted = formatUsPhone(value)
  return formatted.length > 0 ? formatted : undefined
}

function ContactPhoneCell({
  variant,
  control,
  setValue,
  fieldErrors,
  field,
  tagSource,
  labelKey,
  onSaveAsContact,
  contactSaved,
}: {
  variant: LocationVariant
  control: Control<LocationFormData>
  setValue?: UseFormSetValue<LocationFormData>
  fieldErrors: SectionErrors
  field: 'phone' | 'mobile'
  tagSource: 'phone' | 'secondary_phone'
  labelKey: string
  onSaveAsContact?: (variant: LocationVariant) => void
  contactSaved?: boolean
}) {
  const { t } = useTranslation()
  return (
    <FieldCell width="min-w-0 flex-1">
      <Controller
        control={control}
        name={path(variant, field)}
        render={({ field: rhf }) => (
          <CustomerSearchCombobox
            phone
            label={t(labelKey)}
            value={rhf.value as string}
            onValueChange={rhf.onChange}
            onCustomerSelect={(customer) => {
              if (setValue)
                applyContactCustomerToLocation(variant, customer, setValue)
            }}
            getTag={(customer) => phoneTag(customer[tagSource])}
            error={!!fieldErrors[field]}
            searchDisabled={contactSaved}
            action={{
              label: t('orderCreate.location.saveAsContact'),
              onClick: onSaveAsContact
                ? () => onSaveAsContact(variant)
                : undefined,
            }}
          />
        )}
      />
    </FieldCell>
  )
}

function ContactPhoneMobileRow(
  props: SectionProps & { fieldErrors: SectionErrors },
) {
  const { variant, control, setValue, fieldErrors } = props
  return (
    <FieldRow>
      <ContactCell {...props} />
      <ContactPhoneCell
        variant={variant}
        control={control}
        setValue={setValue}
        fieldErrors={fieldErrors}
        field="phone"
        tagSource="phone"
        labelKey="orderCreate.location.phone"
        onSaveAsContact={props.onSaveAsContact}
        contactSaved={props.contactSaved}
      />
      <ContactPhoneCell
        variant={variant}
        control={control}
        setValue={setValue}
        fieldErrors={fieldErrors}
        field="mobile"
        tagSource="secondary_phone"
        labelKey="orderCreate.location.mobile"
        onSaveAsContact={props.onSaveAsContact}
        contactSaved={props.contactSaved}
      />
    </FieldRow>
  )
}

function NoteRow({
  variant,
  register,
  fieldErrors,
}: {
  variant: LocationVariant
  register: UseFormRegister<LocationFormData>
  fieldErrors: SectionErrors
}) {
  const { t } = useTranslation()
  return (
    <FieldRow>
      <FieldCell width="min-w-0 flex-1">
        <Input
          {...FIELD_PROPS}
          {...register(path(variant, 'additionalNote'))}
          label={t('orderCreate.location.additionalNote')}
          error={!!fieldErrors.additionalNote}
        />
      </FieldCell>
    </FieldRow>
  )
}

function CompactLocationRow({
  variant,
  control,
  register,
  setValue,
  errors,
}: {
  variant: LocationVariant
  control: Control<LocationFormData>
  register: UseFormRegister<LocationFormData>
  setValue?: UseFormSetValue<LocationFormData>
  errors: FieldErrors<LocationFormData>
}) {
  const fieldErrors: SectionErrors = errors[variant] ?? {}
  const Icon = variant === 'origin' ? OriginIcon : DestinationIcon
  const iconColor = variant === 'origin' ? 'text-blue-500' : 'text-orange-500'
  return (
    <div className="flex items-end gap-2.5 px-2.5">
      <div className="flex h-8 shrink-0 items-center pb-2">
        <Icon className={cn('size-4', iconColor)} />
      </div>
      <div className="min-w-0 flex-1">
        <CityStateZipRow
          variant={variant}
          control={control}
          register={register}
          setValue={setValue}
          fieldErrors={fieldErrors}
        />
      </div>
    </div>
  )
}

const BUSINESS_NAME_KEY: Record<LocationVariant, ManageFieldKey> = {
  origin: MANAGE_FIELD_KEY.originBusinessName,
  destination: MANAGE_FIELD_KEY.destinationBusinessName,
}

const NOTE_KEY: Record<LocationVariant, ManageFieldKey> = {
  origin: MANAGE_FIELD_KEY.originNote,
  destination: MANAGE_FIELD_KEY.destinationNote,
}

function ContactSummary({
  contact,
  phone,
  onExpand,
}: {
  contact?: string
  phone?: string
  onExpand: () => void
}) {
  const { t } = useTranslation()
  const summary = [contact, formatUsPhone(phone)].filter(Boolean).join(' · ')
  return (
    <button
      type="button"
      onClick={onExpand}
      className="flex h-8 w-full cursor-pointer items-center justify-between gap-2.5"
    >
      <span className="truncate text-sm text-gray-900">{summary}</span>
      <span className="flex shrink-0 items-center gap-1 text-sm text-gray-500 transition-colors hover:text-gray-900">
        {t('orderCreate.location.moreDetails')}
        <ChevronDownIcon className="size-2.5 shrink-0" />
      </span>
    </button>
  )
}

function ContactAccordion(
  props: SectionProps & { fieldErrors: SectionErrors; showNote: boolean },
) {
  const { variant, control, register, collapsibleContacts, showNote } = props
  const contact = useWatch({
    control,
    name: path(variant, 'contact'),
  }) as string
  const phone = useWatch({ control, name: path(variant, 'phone') }) as string
  const mobile = useWatch({ control, name: path(variant, 'mobile') }) as string
  const note = useWatch({
    control,
    name: path(variant, 'additionalNote'),
  }) as string
  // Auto-collapse a block that already has contact data; empty blocks stay
  // open. Captured once on mount — typing never re-collapses (expand-only).
  const [expanded, setExpanded] = React.useState(
    () => !(contact || phone || mobile || note),
  )
  if (collapsibleContacts && !expanded) {
    return (
      <ContactSummary
        contact={contact}
        phone={phone}
        onExpand={() => setExpanded(true)}
      />
    )
  }
  return (
    <div className="flex flex-col gap-1.75">
      <ContactPhoneMobileRow {...props} />
      {showNote && (
        <NoteRow
          variant={variant}
          register={register}
          fieldErrors={props.fieldErrors}
        />
      )}
    </div>
  )
}

function LocationSection(props: SectionProps) {
  const { variant, control, register, setValue, errors, collapsibleContacts } =
    props
  const fieldErrors: SectionErrors = errors[variant] ?? {}
  const showBusinessName = !useIsFieldHidden(BUSINESS_NAME_KEY[variant])
  const showNote = !useIsFieldHidden(NOTE_KEY[variant])
  const fields = (
    <div
      className={cn('flex flex-col', collapsibleContacts ? 'gap-2.5' : 'gap-5')}
    >
      <div className="flex flex-col gap-1.75">
        <BusinessAddressRow {...props} fieldErrors={fieldErrors} />
        <CityStateZipRow
          variant={variant}
          control={control}
          register={register}
          setValue={setValue}
          fieldErrors={fieldErrors}
          withType={showBusinessName}
        />
      </div>
      <ContactAccordion
        {...props}
        fieldErrors={fieldErrors}
        showNote={showNote}
      />
    </div>
  )
  // Full-page keeps the titled SectionHeader; the sidebar uses the pin gutter.
  if (collapsibleContacts)
    return <GutterSection variant={variant}>{fields}</GutterSection>
  return (
    <div className="flex flex-col">
      <SectionHeader variant={variant} />
      <div className="px-2.5">{fields}</div>
    </div>
  )
}

function CityZipOnlySection({
  variant,
  control,
  register,
  setValue,
  errors,
  withType = false,
  collapsibleContacts,
}: {
  variant: LocationVariant
  control: Control<LocationFormData>
  register: UseFormRegister<LocationFormData>
  setValue?: UseFormSetValue<LocationFormData>
  errors: FieldErrors<LocationFormData>
  withType?: boolean
  collapsibleContacts?: boolean
}) {
  const fieldErrors: SectionErrors = errors[variant] ?? {}
  const row = (
    <CityStateZipRow
      variant={variant}
      control={control}
      register={register}
      setValue={setValue}
      fieldErrors={fieldErrors}
      withType={withType}
    />
  )
  // Full-page keeps the titled SectionHeader; the sidebar uses the pin gutter.
  if (collapsibleContacts)
    return <GutterSection variant={variant}>{row}</GutterSection>
  return (
    <div className="flex flex-col">
      <SectionHeader variant={variant} />
      <div className="flex flex-col px-2.5">{row}</div>
    </div>
  )
}

// eslint-disable-next-line max-lines-per-function, complexity
export function LocationCard({
  control,
  register,
  setValue,
  clearErrors,
  errors,
  businessOptions = [],
  contactOptions = [],
  bare = false,
  compact = false,
  cityZipOnly = false,
  cityZipTypeOnly = false,
  onSameAsCustomer,
  flags,
  collapsibleContacts,
}: LocationCardProps) {
  const { t } = useTranslation()
  const flagsResolved = flags ?? NOOP_FLAGS
  const Container = bare ? 'div' : 'section'
  // Sidebar (collapsibleContacts) uses a tighter block-to-block gap than the
  // roomier full-page create form.
  const shellClass = bare
    ? cn('flex w-full flex-col', collapsibleContacts ? 'gap-5' : 'gap-10')
    : 'flex w-full flex-col gap-10 rounded-md border border-gray-200 bg-white px-2.5 pt-2.5 pb-5'

  if (compact) {
    return (
      <div className="flex w-full flex-col gap-2.5 pb-1.25">
        <div className="flex h-8 items-center px-2.5">
          <span className="text-xs uppercase text-gray-500">
            {t('orderCreate.location.routeTitle')}
          </span>
        </div>
        <CompactLocationRow
          variant="origin"
          control={control}
          register={register}
          setValue={setValue}
          errors={errors}
        />
        <CompactLocationRow
          variant="destination"
          control={control}
          register={register}
          setValue={setValue}
          errors={errors}
        />
      </div>
    )
  }

  if (cityZipOnly || cityZipTypeOnly) {
    const compactShell = bare
      ? 'flex w-full flex-col gap-2.5'
      : 'flex w-full flex-col gap-2.5 rounded-md border border-gray-200 bg-white px-2.5 pt-2.5 pb-5'
    return (
      <Container className={compactShell}>
        <CityZipOnlySection
          variant="origin"
          control={control}
          register={register}
          setValue={setValue}
          errors={errors}
          withType={cityZipTypeOnly}
          collapsibleContacts={collapsibleContacts}
        />
        <CityZipOnlySection
          variant="destination"
          control={control}
          register={register}
          setValue={setValue}
          errors={errors}
          withType={cityZipTypeOnly}
          collapsibleContacts={collapsibleContacts}
        />
      </Container>
    )
  }

  const sectionProps = (variant: LocationVariant): SectionProps => ({
    variant,
    control,
    register,
    setValue,
    clearErrors,
    errors,
    businessOptions,
    contactOptions,
    onSameAsCustomer,
    onSaveAsTerminal: flagsResolved.onSaveAsTerminal,
    onSaveAsContact: flagsResolved.onSaveAsContact,
    onClearTerminal: flagsResolved.onClearTerminal,
    onClearContact: flagsResolved.onClearContact,
    terminalSaved: flagsResolved.terminalSaved[variant],
    contactSaved: flagsResolved.contactSaved[variant],
    collapsibleContacts,
  })

  return (
    <Container className={shellClass}>
      <LocationSection {...sectionProps('origin')} />
      <LocationSection {...sectionProps('destination')} />
    </Container>
  )
}
